/**
 * Reservation share helpers (#746).
 *
 * - 共有テキスト生成 (絵文字込み、i18n template に埋め込む)
 * - .ics (iCalendar) 生成 — Google / Apple カレンダーに「追加」できる
 * - 短縮 URL 組み立て (`/rsv/{shortCode}` — #1261: `/r/:transactionId` 公開レシートに
 *   ルートがシャドウされていたため prefix を分離)
 *
 * Web Share API そのものは ReservationShareSheet 側で navigator.share を呼ぶ。
 * 本 module は副作用なし、テストしやすい純粋関数の集まり。
 */

import type { Reservation } from '../store/useStore'

export interface ShareableReservation
  extends Pick<Reservation, 'date' | 'time' | 'partySize' | 'courseName' | 'paymentTiming' | 'shortCode'> {
  storeName: string
  storeAddress?: string
}

export function buildReservationShareUrl(shortCode: string | undefined): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://yokosopay.com'
  return shortCode ? `${base}/rsv/${shortCode}` : base
}

/**
 * Format the JST datetime fragment used both in the share text and the .ics
 * UTC timestamps. Reservation date/time は店舗 local (JST 固定) で保存されている。
 */
function toJstDate(r: Pick<Reservation, 'date' | 'time'>): Date {
  // `2026-06-20T19:00:00+09:00` — explicit JST offset to avoid UA tz drift.
  return new Date(`${r.date}T${r.time}:00+09:00`)
}

/**
 * iCalendar (.ics) 文字列を組み立てる。依存ライブラリなし、RFC 5545 の最小サブセット。
 * - DTSTART / DTEND は UTC (Z 末尾) で記述 (= 実装ばらつきが少ない)
 * - 予約 duration が分からない場合 (= courseDuration が未取得時) は 2h を仮置き
 * - DESCRIPTION 内の改行は `\n` literal でエスケープ
 */
export interface IcsInput extends ShareableReservation {
  /** 予約 duration (分)。未指定なら 120 分。 */
  durationMinutes?: number
  /** 共有 URL (= /r/{shortCode}) を DESCRIPTION 末尾と URL field に入れる。 */
  shareUrl?: string
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}

function toIcsUtc(d: Date): string {
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T` +
    `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  )
}

/**
 * RFC 5545 escape: `\` → `\\`, `;` → `\;`, `,` → `\,`, newline → `\n`.
 */
function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

export function buildReservationIcs(input: IcsInput): string {
  const start = toJstDate(input)
  const end = new Date(start.getTime() + (input.durationMinutes ?? 120) * 60_000)
  const uid = `${input.shortCode || `${input.date}-${input.time}`}@yokoso-pay`
  const dtstamp = toIcsUtc(new Date())

  const summary = input.courseName
    ? `${input.storeName} - ${input.courseName}`
    : input.storeName
  const descriptionParts = [
    `${input.partySize}名`,
    input.courseName ? `コース: ${input.courseName}` : '',
    input.shareUrl ? `詳細: ${input.shareUrl}` : '',
  ].filter(Boolean)

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//YOKOSO PAY//Reservation//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${icsEscape(summary)}`,
    `DESCRIPTION:${icsEscape(descriptionParts.join('\n'))}`,
    input.storeAddress ? `LOCATION:${icsEscape(input.storeAddress)}` : '',
    input.shareUrl ? `URL:${input.shareUrl}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean)

  // RFC 5545 は line ending CRLF。Google / Apple は LF も受け付けるが、互換最大化。
  return lines.join('\r\n') + '\r\n'
}

/**
 * Google Calendar の「予定を作成」フォームを pre-fill した URL を返す。
 * 別タブで開けば Google Calendar に直接予定追加できる (= デスクトップで .ics を
 * Outlook が掴んでしまう問題の回避策。訪日外国人は Google ユーザーが大半)。
 *
 * 仕様: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
 */
export function buildGoogleCalendarUrl(input: IcsInput): string {
  const start = toJstDate(input)
  const end = new Date(start.getTime() + (input.durationMinutes ?? 120) * 60_000)
  // GCal は YYYYMMDDTHHmmssZ 形式 (UTC)
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T` +
    `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`

  const text = input.courseName
    ? `${input.storeName} - ${input.courseName}`
    : input.storeName
  const detailsLines = [
    `${input.partySize}名`,
    input.courseName ? `コース: ${input.courseName}` : '',
    input.shareUrl ? `詳細: ${input.shareUrl}` : '',
  ].filter(Boolean)

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: detailsLines.join('\n'),
  })
  if (input.storeAddress) params.set('location', input.storeAddress)

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/**
 * Google Calendar URL を新規タブで開く。
 */
export function openGoogleCalendar(input: IcsInput): void {
  if (typeof window === 'undefined') return
  window.open(buildGoogleCalendarUrl(input), '_blank', 'noopener,noreferrer')
}

/**
 * Browser 限定: blob を ICS file としてダウンロードさせる。
 * Web Share API + files をサポートするブラウザでは ReservationShareSheet 側
 * で `navigator.canShare({ files: [...] })` 経由でも共有可能だが、
 * デフォルトはダウンロード経由でカレンダーアプリに「開く」させる方が確実。
 */
export function downloadReservationIcs(input: IcsInput, filename = 'reservation.ics'): void {
  if (typeof window === 'undefined') return
  const blob = new Blob([buildReservationIcs(input)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // 即 revoke すると Safari がダウンロード処理する前に消えるので 1s 後に解放。
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
